import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, RadarChart, PolarGrid,
  PolarAngleAxis as PolarAngleAxisBase, PolarRadiusAxis as PolarRadiusAxisBase,
  Radar, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
} from 'recharts';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetNationalHolisticAnalyticsQuery,
  deriveRecordMetrics,
  mergeNationalAnalyticsResponses,
  TermFilter,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import KVSLoader from '../../../../../components/loaders/KVSLoader';
import { StatBox } from '../../../components/shared';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarAngleAxis = PolarAngleAxisBase as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PolarRadiusAxis = PolarRadiusAxisBase as any;

const COLORS = ['#4F46E5', '#6366F1', '#818CF8', '#A5B4FC'];

const BehaviorAnalysis: React.FC = () => {
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');
  const isOverall = term === 'overall';

  const singleTermQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!effectiveData?.data) return [];
    return effectiveData.data.flatMap((region) => region.records || []);
  }, [effectiveData]);



  const stats = useMemo(() => {
    if (!records.length) {
      return {
        avgBehavior: 0,
        avgPeer: 0,
        avgAcademic: 0,
        radarData: [
          { subject: 'Confidence', value: 0 },
          { subject: 'Focus', value: 0 },
          { subject: 'Motivation', value: 0 },
          { subject: 'Leadership', value: 0 },
        ],
        distribution: [
          { range: '90-100', count: 0 },
          { range: '70-89', count: 0 },
          { range: '50-69', count: 0 },
          { range: '<50', count: 0 },
        ],
      };
    }

    let totalBehavior = 0;
    let totalPeer = 0;
    let totalAcademic = 0;
    let totalConfidence = 0;
    let totalFocus = 0;
    let totalMotivation = 0;
    let totalLeadership = 0;

    let dist90_100 = 0;
    let dist70_89 = 0;
    let dist50_69 = 0;
    let distUnder50 = 0;

    records.forEach((record) => {
      const metrics = deriveRecordMetrics(record, term);
      totalBehavior += metrics.behavior;
      totalPeer += metrics.peer;
      totalAcademic += metrics.competency;

      totalConfidence += Number(record.confidence_score || 0);
      totalFocus += Number(record.focus_score || 0);
      totalMotivation += Number(record.motivation_score || 0);
      totalLeadership += Number(record.leadership_score || 0);

      const bScore = metrics.behavior;
      if (bScore >= 90) dist90_100++;
      else if (bScore >= 70) dist70_89++;
      else if (bScore >= 50) dist50_69++;
      else distUnder50++;
    });

    const len = records.length;
    return {
      avgBehavior: Math.round(totalBehavior / len),
      avgPeer: Math.round(totalPeer / len),
      avgAcademic: Math.round(totalAcademic / len),
      radarData: [
        { subject: 'Confidence', value: Math.round(totalConfidence / len) },
        { subject: 'Focus', value: Math.round(totalFocus / len) },
        { subject: 'Motivation', value: Math.round(totalMotivation / len) },
        { subject: 'Leadership', value: Math.round(totalLeadership / len) },
      ],
      distribution: [
        { range: '90-100', count: dist90_100 },
        { range: '70-89', count: dist70_89 },
        { range: '50-69', count: dist50_69 },
        { range: '<50', count: distUnder50 },
      ],
    };
  }, [records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Behavior Analysis</h1>
          <p className="text-sm text-gray-500">Aggregate behavior patterns across {records.length} students</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatBox label="Avg Behavior Score" value={`${stats.avgBehavior}`} color="text-indigo-600" large />
        <StatBox label="Avg Peer Score" value={`${stats.avgPeer}`} color="text-blue-600" large />
        <StatBox label="Avg Academic" value={`${stats.avgAcademic}%`} color="text-emerald-600" large />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Overall Behavior Radar</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={stats.radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 12 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar name="Average" dataKey="value" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.2} strokeWidth={2} />
              <Tooltip formatter={(value: any) => [`${value}/100`, 'Avg Score']} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Behavior Score Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.distribution} barSize={48}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="range" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {stats.distribution.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default BehaviorAnalysis;

